import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/context/AuthContext'
import { registerSchema } from '@/utils/validation'
import { ROUTES } from '@/config/constants'
import type { RegisterInput } from '@/types/auth'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { setEmailPrefs } from '@/api/game'

const RegisterPage = () => {
  const { t } = useTranslation()
  const { register: registerUser, confirmEmail, loginWithGoogle } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [needsConfirmation, setNeedsConfirmation] = useState(false)
  const [email, setEmail] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')
  const [emailOptIn, setEmailOptIn] = useState(true)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  })

  const onSubmit = async (data: RegisterInput) => {
    try {
      setIsLoading(true)
      setError('')
      setEmail(data.email)
      await registerUser(data)
      try { await setEmailPrefs(emailOptIn) } catch { /* non-blocking */ }
      navigate(ROUTES.HUB)
    } catch (err: any) {
      if (err.message === 'CONFIRM_EMAIL_REQUIRED') {
        setNeedsConfirmation(true)
        setError(t('auth.checkEmailForCode'))
      } else {
        setError(err.message || t('auth.registrationFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onConfirmEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setIsLoading(true)
      setError('')
      await confirmEmail(email, confirmationCode)
      navigate(ROUTES.LOGIN)
    } catch (err: any) {
      setError(err.message || t('auth.confirmationFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  if (needsConfirmation) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-center mb-6">{t('auth.confirmEmail')}</h2>

        {error && (
          <div className="bg-status-info/10 border border-status-info text-status-info px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <p className="text-text-secondary mb-4 text-center">
          {t('auth.sentCodeTo', { email })}
        </p>

        <form onSubmit={onConfirmEmail} className="space-y-4">
          <Input
            label={t('auth.confirmationCode')}
            type="text"
            placeholder="123456"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
          />

          <Button type="submit" className="w-full" isLoading={isLoading}>
            {t('auth.confirmEmailBtn')}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-text-secondary">
          <button
            onClick={() => setNeedsConfirmation(false)}
            className="text-primary-blue hover:underline font-medium"
          >
            {t('auth.backToRegistration')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-center mb-6">{t('auth.createAccount')}</h2>

      {error && (
        <div className="bg-status-error/10 border border-status-error text-status-error px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label={t('auth.email')}
          type="email"
          placeholder="your@email.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label={t('auth.username')}
          type="text"
          placeholder="coolplayer123"
          error={errors.username?.message}
          {...register('username')}
        />

        <Input
          label={t('auth.password')}
          type="password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...register('password')}
        />

        <Input
          label={t('auth.confirmPassword')}
          type="password"
          placeholder="••••••••"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="emailOptIn"
            checked={emailOptIn}
            onChange={(e) => setEmailOptIn(e.target.checked)}
            className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
          />
          <label htmlFor="emailOptIn" className="text-sm text-text-secondary cursor-pointer">
            {t('auth.emailOptIn')}
          </label>
        </div>

        <Button type="submit" className="w-full" isLoading={isLoading}>
          {t('auth.createAccount')}
        </Button>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-text-secondary">{t('auth.or')}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={loginWithGoogle}
        className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {t('auth.signUpGoogle')}
      </button>

      <div className="mt-6 text-center text-sm text-text-secondary">
        {t('auth.alreadyHaveAccount')}{' '}
        <Link to={ROUTES.LOGIN} className="text-primary-blue hover:underline font-medium">
          {t('auth.login')}
        </Link>
      </div>
    </div>
  )
}

export default RegisterPage